import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SharedModule } from '@/shared.module';
import { NnnService } from '@/services';
import type { NocIndicator, NocScoreValue, NocZorgresultaatSummary } from '@/services';
import type {
  EditNnnResultDialogData,
  EditNnnResultDialogResult,
  NnnKnowledgeDomein,
  NnnKnowledgeKlasse,
} from '../../types/careplan-nnn.types';

type ResultFilter = 'linked' | 'all';

/**
 * Dialog to add a NOC zorgresultaat to a careplan-NNN row, or to edit one
 * already on the row: the indicator selection, the current/desired score of
 * the zorgresultaat and the current/desired score per indicator. When the row's NANDA
 * diagnosis is known, the list defaults to the zorgresultaten linked to that
 * diagnosis, with a toggle to show all. Closes with an
 * {@link EditNnnResultDialogResult}, or `undefined` on cancel.
 */
@Component({
  selector: 'app-edit-nnn-result-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './edit-nnn-result-dialog.component.html',
  styleUrl: './edit-nnn-result-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditNnnResultDialogComponent implements OnInit {
  private readonly nnnService = inject(NnnService);
  private readonly data = inject<EditNnnResultDialogData>(MAT_DIALOG_DATA);

  protected readonly isEdit = !!this.data.zorgresultaat;

  protected readonly zorgresultaten = signal<NocZorgresultaatSummary[]>([]);
  protected readonly resultFilter = signal<ResultFilter>(this.data.diagnoseCode != null ? 'linked' : 'all');
  private readonly linkedCodes = signal<ReadonlySet<number> | null>(null);
  protected readonly searchText = signal('');
  protected readonly selected = signal<NocZorgresultaatSummary | null>(this.data.zorgresultaat ?? null);
  protected readonly indicators = signal<NocIndicator[]>([]);
  protected readonly checkedRefs = signal<ReadonlySet<string>>(new Set(this.data.selectedIndicatorRefs ?? []));
  protected readonly loadingList = signal(false);
  protected readonly loadingIndicators = signal(false);

  protected readonly loadingTree = signal(false);
  protected readonly nocDomeinen = signal<NnnKnowledgeDomein[]>([]);
  private readonly itemsByKlasse = signal<ReadonlyMap<string, NocZorgresultaatSummary[]>>(new Map());
  private readonly loadingKlassen = signal<ReadonlySet<string>>(new Set());
  private readonly expandedDomeinen = signal<ReadonlySet<string>>(new Set());
  private readonly expandedKlassen = signal<ReadonlySet<string>>(new Set());
  private readonly klasseLoads = new Map<string, Promise<void>>();
  private treeLoad: Promise<void> | null = null;
  private readonly excludeRefs = new Set(this.data.excludeRefs ?? []);

  protected readonly currentScoreRef = signal<string | null>(this.data.currentScoreRef ?? null);
  protected readonly desiredScoreRef = signal<string | null>(this.data.desiredScoreRef ?? null);
  protected readonly indicatorScoreRefs = signal<ReadonlyMap<string, string | null>>(
    new Map(Object.entries(this.data.indicatorScoreRefs ?? {}))
  );
  protected readonly indicatorDesiredScoreRefs = signal<ReadonlyMap<string, string | null>>(
    new Map(Object.entries(this.data.indicatorDesiredScoreRefs ?? {}))
  );
  private readonly outcomeScoreSchaalId = signal<string | null>(null);
  private readonly scoreValuesBySchaal = signal<ReadonlyMap<string, NocScoreValue[]>>(new Map());
  private readonly loadedSchaalIds = new Set<string>();

  protected readonly outcomeScoreValues = computed(() => {
    const schaalId = this.outcomeScoreSchaalId();
    return schaalId ? (this.scoreValuesBySchaal().get(schaalId) ?? []) : [];
  });

  protected readonly showFilterToggle = computed(() => (this.linkedCodes()?.size ?? 0) > 0);

  protected readonly showTree = computed(() => this.resultFilter() === 'all' && this.searchText().trim().length === 0);

  protected readonly filteredZorgresultaten = computed(() => {
    const query = this.searchText().trim().toLowerCase();
    const linked = this.resultFilter() === 'linked' ? this.linkedCodes() : null;
    return this.zorgresultaten().filter(
      z =>
        !this.excludeRefs.has(z.id) &&
        (!linked || linked.has(z.code)) &&
        (!query || z.omschrijving.toLowerCase().includes(query) || z.code.toString().includes(query))
    );
  });

  constructor() {
    effect(() => {
      if (!this.isEdit && this.resultFilter() === 'all') {
        untracked(() => void this.ensureNocTree());
      }
    });
  }

  ngOnInit(): void {
    if (!this.isEdit) {
      void this.loadZorgresultaten();
      if (this.data.diagnoseCode != null) {
        void this.loadLinkedZorgresultaten(this.data.diagnoseCode);
      }
    }
    const preset = this.selected();
    if (preset) {
      void this.loadIndicatoren(preset);
      void this.loadOutcomeScoreSchaal(preset);
    }
  }

  private async loadZorgresultaten(): Promise<void> {
    this.loadingList.set(true);
    const { data } = await this.nnnService.getNocOutcomes(undefined, true);
    this.loadingList.set(false);
    if (data) {
      this.zorgresultaten.set(data);
    }
  }

  private async loadLinkedZorgresultaten(diagnoseCode: number): Promise<void> {
    const { data } = await this.nnnService.getNandaDiagnoseZorgresultaten(diagnoseCode);
    if (data && data.length > 0) {
      this.linkedCodes.set(new Set(data.map(z => z.code)));
    } else {
      this.resultFilter.set('all');
    }
  }

  protected onFilterChange(filter: ResultFilter): void {
    this.resultFilter.set(filter);
  }

  private ensureNocTree(): Promise<void> {
    if (this.nocDomeinen().length > 0) {
      return Promise.resolve();
    }
    this.treeLoad ??= this.loadNocTree().finally(() => {
      this.treeLoad = null;
    });
    return this.treeLoad;
  }

  private async loadNocTree(): Promise<void> {
    this.loadingTree.set(true);
    const [domeinen, klassen] = await Promise.all([this.nnnService.getNocDomeinen(), this.nnnService.getNocKlassen()]);
    this.loadingTree.set(false);
    if (!domeinen.data || !klassen.data) {
      return;
    }
    const klasseData = klassen.data;
    this.nocDomeinen.set(
      domeinen.data.map(domein => ({
        key: `noc:${domein.id}`,
        label: String(domein.nummer ?? ''),
        omschrijving: domein.omschrijving,
        definitie: domein.definitie,
        klassen: klasseData
          .filter(klasse => klasse.domeinId === domein.id)
          .map(klasse => ({
            key: `noc:${klasse.id}`,
            label: klasse.code,
            omschrijving: klasse.omschrijving,
            definitie: klasse.definitie,
          })),
      }))
    );
  }

  protected itemsFor(klasseKey: string): NocZorgresultaatSummary[] | undefined {
    return this.itemsByKlasse().get(klasseKey);
  }

  protected isLoadingKlasse(klasseKey: string): boolean {
    return this.loadingKlassen().has(klasseKey);
  }

  protected isDomeinExpanded(domeinKey: string): boolean {
    return this.expandedDomeinen().has(domeinKey);
  }

  protected isKlasseExpanded(klasseKey: string): boolean {
    return this.expandedKlassen().has(klasseKey);
  }

  protected setDomeinExpanded(domeinKey: string, expanded: boolean): void {
    this.expandedDomeinen.update(keys => toggleKey(keys, domeinKey, expanded));
  }

  protected setKlasseExpanded(klasse: NnnKnowledgeKlasse, expanded: boolean): void {
    this.expandedKlassen.update(keys => toggleKey(keys, klasse.key, expanded));
    if (expanded) {
      void this.ensureKlasseItems(klasse);
    }
  }

  private ensureKlasseItems(klasse: NnnKnowledgeKlasse): Promise<void> {
    if (this.itemsByKlasse().has(klasse.key)) {
      return Promise.resolve();
    }
    let load = this.klasseLoads.get(klasse.key);
    if (!load) {
      load = this.loadKlasseItems(klasse).finally(() => this.klasseLoads.delete(klasse.key));
      this.klasseLoads.set(klasse.key, load);
    }
    return load;
  }

  private async loadKlasseItems(klasse: NnnKnowledgeKlasse): Promise<void> {
    this.loadingKlassen.update(keys => toggleKey(keys, klasse.key, true));
    const klasseId = Number(klasse.key.split(':')[1]);
    const { data } = await this.nnnService.getNocOutcomes(undefined, true, klasseId);
    this.loadingKlassen.update(keys => toggleKey(keys, klasse.key, false));
    if (data) {
      this.itemsByKlasse.update(items =>
        new Map(items).set(
          klasse.key,
          data.filter(z => !this.excludeRefs.has(z.id))
        )
      );
    }
  }

  private async loadIndicatoren(zorgresultaat: NocZorgresultaatSummary): Promise<void> {
    this.loadingIndicators.set(true);
    const { data } = await this.nnnService.getNocIndicatoren(zorgresultaat.code);
    this.loadingIndicators.set(false);
    if (data && this.selected()?.code === zorgresultaat.code) {
      this.indicators.set(data);
      const schaalIds = new Set(data.map(indicator => indicator.scoreSchaalId).filter((id): id is string => !!id));
      for (const schaalId of schaalIds) {
        void this.loadScoreValues(schaalId);
      }
    }
  }

  private async loadOutcomeScoreSchaal(zorgresultaat: NocZorgresultaatSummary): Promise<void> {
    const { data } = await this.nnnService.getNocOutcomeDetail(zorgresultaat.code);
    if (!data?.scoreSchaalId || this.selected()?.code !== zorgresultaat.code) {
      return;
    }
    this.outcomeScoreSchaalId.set(data.scoreSchaalId);
    void this.loadScoreValues(data.scoreSchaalId);
  }

  private async loadScoreValues(schaalId: string): Promise<void> {
    if (this.loadedSchaalIds.has(schaalId)) {
      return;
    }
    this.loadedSchaalIds.add(schaalId);
    const { data } = await this.nnnService.getNocScoreValues(schaalId);
    if (data) {
      this.scoreValuesBySchaal.update(values => new Map(values).set(schaalId, data));
    }
  }

  protected onSearchInput(event: Event): void {
    this.searchText.set((event.target as HTMLInputElement).value);
  }

  protected select(zorgresultaat: NocZorgresultaatSummary): void {
    if (this.selected()?.code === zorgresultaat.code) {
      return;
    }
    this.selected.set(zorgresultaat);
    this.indicators.set([]);
    this.checkedRefs.set(new Set());
    this.currentScoreRef.set(null);
    this.desiredScoreRef.set(null);
    this.indicatorScoreRefs.set(new Map());
    this.indicatorDesiredScoreRefs.set(new Map());
    this.outcomeScoreSchaalId.set(null);
    void this.loadIndicatoren(zorgresultaat);
    void this.loadOutcomeScoreSchaal(zorgresultaat);
  }

  protected isChecked(indicator: NocIndicator): boolean {
    return this.checkedRefs().has(String(indicator.code));
  }

  protected toggleIndicator(indicator: NocIndicator): void {
    const ref = String(indicator.code);
    this.checkedRefs.update(refs => {
      const next = new Set(refs);
      if (next.has(ref)) {
        next.delete(ref);
      } else {
        next.add(ref);
      }
      return next;
    });
  }

  protected indicatorScoreValues(indicator: NocIndicator): NocScoreValue[] {
    return indicator.scoreSchaalId ? (this.scoreValuesBySchaal().get(indicator.scoreSchaalId) ?? []) : [];
  }

  protected indicatorScore(indicator: NocIndicator): string | null {
    return this.indicatorScoreRefs().get(String(indicator.code)) ?? null;
  }

  protected indicatorDesiredScore(indicator: NocIndicator): string | null {
    return this.indicatorDesiredScoreRefs().get(String(indicator.code)) ?? null;
  }

  protected setCurrentScore(scoreRef: string | null): void {
    this.currentScoreRef.set(scoreRef);
  }

  protected setDesiredScore(scoreRef: string | null): void {
    this.desiredScoreRef.set(scoreRef);
  }

  protected setIndicatorScore(indicator: NocIndicator, scoreRef: string | null): void {
    this.indicatorScoreRefs.update(scores => new Map(scores).set(String(indicator.code), scoreRef));
  }

  protected setIndicatorDesiredScore(indicator: NocIndicator, scoreRef: string | null): void {
    this.indicatorDesiredScoreRefs.update(scores => new Map(scores).set(String(indicator.code), scoreRef));
  }

  protected buildResult(): EditNnnResultDialogResult | null {
    const zorgresultaat = this.selected();
    if (!zorgresultaat) {
      return null;
    }
    const checked = this.checkedRefs();
    const knownRefs = this.indicators()
      .map(indicator => String(indicator.code))
      .filter(ref => checked.has(ref));
    const unknownRefs = [...checked].filter(ref => !knownRefs.includes(ref));
    const indicatorRefs = [...knownRefs, ...unknownRefs];
    const scores = this.indicatorScoreRefs();
    const desiredScores = this.indicatorDesiredScoreRefs();
    return {
      zorgresultaat,
      indicatorRefs,
      indicators: this.indicators(),
      currentScoreRef: this.currentScoreRef(),
      desiredScoreRef: this.desiredScoreRef(),
      indicatorScoreRefs: Object.fromEntries(indicatorRefs.map(ref => [ref, scores.get(ref) ?? null])),
      indicatorDesiredScoreRefs: Object.fromEntries(indicatorRefs.map(ref => [ref, desiredScores.get(ref) ?? null])),
      scoreValues: [...this.scoreValuesBySchaal().values()].flat(),
    };
  }
}

function toggleKey(keys: ReadonlySet<string>, key: string, present: boolean): ReadonlySet<string> {
  const next = new Set(keys);
  if (present) {
    next.add(key);
  } else {
    next.delete(key);
  }
  return next;
}
